<template>
  <div class="cust-page py-3">
    <header class="cust-toolbar">
      <div class="cust-toolbar__title">
        <span class="cust-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>{{ customerName || 'Customer details' }}</h1>
          <p>Profile, history, and CRM activity for customer #{{ customerId }}</p>
        </div>
      </div>
      <div class="cust-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="cust-updated">Updated {{ lastUpdatedLabel }}</span>
        <CButton size="sm" class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="back-btn__icon" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <span>Back to Customers</span>
        </CButton>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="reloadAll"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          v-if="!isModerator"
          color="primary"
          size="sm"
          class="create-btn"
          @click="openAddTicketModal"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Add Ticket</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="cust-alert">{{ errorMessage }}</CAlert>

    <!-- Hero / profile card -->
    <section class="cust-hero">
      <div class="cust-hero__avatar" :style="{ background: avatarGradient }">{{ avatarInitials }}</div>
      <div class="cust-hero__main">
        <div class="cust-hero__name">{{ customerName || 'Unnamed customer' }}</div>
        <div class="cust-hero__meta">
          <span v-if="customerPhone" class="cust-chip cust-chip--phone">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            {{ customerPhone }}
          </span>
          <span v-if="customerAddress" class="cust-chip cust-chip--addr">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {{ customerAddress }}
          </span>
        </div>
      </div>
    </section>

    <!-- Summary tiles -->
    <div class="summary-tiles">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        :class="['summary-tile', `summary-tile--${card.key}`]"
      >
        <span class="summary-tile__label">{{ card.label }}</span>
        <span class="summary-tile__value">{{ card.value }}</span>
      </div>
    </div>

    <!-- Section card with pill tabs -->
    <section class="cust-section">
      <header class="cust-section__head">
        <div class="cust-tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="cust-tab"
            :class="{ 'is-active': tab.key === activeTab }"
            :aria-selected="tab.key === activeTab"
            @click="setTab(tab.key)"
          >
            <span class="cust-tab__label">{{ tab.label }}</span>
            <span v-if="tab.count" class="cust-tab__count">{{ tab.count }}</span>
          </button>
        </div>
      </header>

      <div v-if="isLoading" class="cust-section__body cust-loading">
        <CSpinner size="sm" class="mr-2" />
        <span>Loading {{ activeTabLabel.toLowerCase() }}…</span>
      </div>

      <div v-else-if="!activeItems.length" class="cust-section__body cust-empty">
        <div class="cust-empty__title">No {{ activeTabLabel.toLowerCase() }} yet</div>
        <div class="cust-empty__hint">
          {{ activeTab === 'tickets' && !isModerator
            ? 'Use the “Add Ticket” button above to log a customer concern.'
            : `New ${activeTabLabel.toLowerCase()} will appear here.` }}
        </div>
      </div>

      <div v-else>
        <div class="cust-table-wrap">
          <!-- Orders -->
          <table v-if="activeTab === 'orders'" class="cust-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Phone</th>
                <th class="col-num">Items</th>
                <th>Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in tabData.orders.items" :key="order.id || order.order_number">
                <td><span class="mono-strong">{{ order.order_number || '—' }}</span></td>
                <td class="col-date">{{ formatDate(order.order_date) }}</td>
                <td class="col-phone">{{ order.contact_phone || '—' }}</td>
                <td class="col-num">{{ Number(order.item_count || 0) }}</td>
                <td><span :class="['type-pill', orderTypeClass(order.order_type)]">{{ formatLabel(order.order_type) || '—' }}</span></td>
                <td><span :class="['status-pill', shippingStatusClass(order.shipping_status)]">{{ formatLabel(order.shipping_status) || '—' }}</span></td>
              </tr>
            </tbody>
          </table>

          <!-- Calls -->
          <table v-else-if="activeTab === 'calls'" class="cust-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Caller</th>
                <th>Status</th>
                <th>Comments</th>
                <th class="col-num">Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(call, idx) in tabData.calls.items" :key="call.id || `call-${idx}`">
                <td class="col-date">{{ formatDateTime(call.created_at) }}</td>
                <td>{{ call.caller || '—' }}</td>
                <td><span :class="['status-pill', callStatusClass(call.status)]">{{ formatLabel(call.status) || '—' }}</span></td>
                <td class="col-comments">{{ call.comments || '—' }}</td>
                <td class="col-num">{{ formatDuration(call.duration) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Medical Records -->
          <table v-else-if="activeTab === 'medical_records'" class="cust-table">
            <thead>
              <tr>
                <th>Record</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(rec, idx) in tabData.medical_records.items" :key="rec.id || `rec-${idx}`">
                <td>{{ rec.name || '—' }}</td>
                <td class="col-date">{{ formatDateTime(rec.created_at) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Tickets -->
          <table v-else-if="activeTab === 'tickets'" class="cust-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Mood</th>
                <th>Problem</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ticket in tabData.tickets.items" :key="ticket.id">
                <td><span class="mono-strong">#{{ ticket.id }}</span></td>
                <td><span :class="['type-pill', ticketTypeClass(ticket.ticket_type)]">{{ ticket.ticket_type || '—' }}</span></td>
                <td><span :class="['mood-pill', moodClass(ticket.customer_mood)]">{{ ticket.customer_mood || '—' }}</span></td>
                <td class="col-comments">{{ ticket.problem || ticket.description || '—' }}</td>
                <td class="col-date">{{ formatDateTime(ticket.created_at) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Timeline -->
          <table v-else-if="activeTab === 'timeline'" class="cust-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>User</th>
                <th>Detail</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in tabData.timeline.items" :key="`tl-${idx}`">
                <td><span :class="['type-pill', timelineTypeClass(item.type)]">{{ formatLabel(item.type) || '—' }}</span></td>
                <td>{{ item.user || '—' }}</td>
                <td class="col-comments">{{ item.content || '—' }}</td>
                <td class="col-date">{{ formatDateTime(item.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="isPaginatedTab && activeTotalPages > 1" class="cust-pagination">
          <AppPagination
            :value="activePage"
            :pages="activeTotalPages"
            :disabled="isLoading"
            @change="onPageChange"
          />
        </div>
      </div>
    </section>

    <!-- Add Ticket dialog -->
    <AppConfirmDialog
      :visible="addTicketModal"
      title="Create new ticket"
      message="Capture the customer's complaint or feedback for follow-up."
      confirm-label="Save Ticket"
      busy-label="Saving…"
      cancel-label="Cancel"
      variant="info"
      :busy="isSavingTicket"
      @confirm="saveTicket"
      @cancel="cancelAddTicket"
    >
      <div class="form-grid">
        <div class="form-field">
          <label class="form-label">Product used</label>
          <AppDropdown
            :value="newTicket.product_used"
            :options="productDropdownOptions"
            placeholder="Select product"
            @update:value="newTicket.product_used = $event"
          />
        </div>

        <div class="form-field">
          <label class="form-label" for="ticket-crop">Crop</label>
          <input
            id="ticket-crop"
            v-model.trim="newTicket.crop"
            type="text"
            class="form-input"
            placeholder="e.g. Banana"
          />
        </div>

        <div class="form-field">
          <label class="form-label">Ticket type</label>
          <AppDropdown
            :value="newTicket.ticket_type"
            :options="ticketTypeDropdownOptions"
            placeholder="Select type"
            @update:value="onTicketTypeSelect"
          />
        </div>

        <div v-if="newTicket.ticket_type === 'Result'" class="form-field">
          <label class="form-label">Reason</label>
          <AppDropdown
            :value="newTicket.reason"
            :options="reasonDropdownOptions"
            placeholder="Select reason"
            @update:value="newTicket.reason = $event"
          />
        </div>

        <div class="form-field">
          <label class="form-label">Customer mood</label>
          <AppDropdown
            :value="newTicket.customer_mood"
            :options="moodDropdownOptions"
            placeholder="Select mood"
            @update:value="newTicket.customer_mood = $event"
          />
        </div>

        <div class="form-field form-field--check">
          <label class="form-check">
            <input v-model="newTicket.is_fertilizer_user" type="checkbox" />
            <span>Customer uses other fertilizer</span>
          </label>
        </div>

        <div v-if="newTicket.is_fertilizer_user" class="form-field">
          <label class="form-label" for="ticket-desc">Other fertilizer description</label>
          <input
            id="ticket-desc"
            v-model.trim="newTicket.description"
            type="text"
            class="form-input"
            placeholder="Brand or details"
          />
        </div>
      </div>
    </AppConfirmDialog>

    <!-- Result dialog -->
    <AppConfirmDialog
      :visible="resultModal.show"
      :title="resultModal.title || 'Notice'"
      :message="resultModal.message"
      :variant="resultModalVariant"
      confirm-label="OK"
      cancel-label=""
      @confirm="closeResultModal"
      @cancel="closeResultModal"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import AppPagination from '@/components/AppPagination.vue'
import AppDropdown from '@/components/AppDropdown.vue'

const ALLOWED_TABS = ['orders', 'calls', 'medical_records', 'tickets', 'timeline']
const PAGINATED_TABS = ['orders', 'calls', 'medical_records']

const TAB_LABELS = {
  orders: 'Orders',
  calls: 'Calls',
  medical_records: 'Medical Records',
  tickets: 'Tickets',
  timeline: 'Timeline'
}

const PRODUCT_OPTIONS = ['THATCH', 'MIGRANIX', 'KNORHIN', 'STAM']
const TICKET_TYPE_OPTIONS = ['No Result', 'Result', 'Other Complaint']
const REASON_OPTIONS = ['Improper usage', 'Course Not Completed', 'Continuing', 'Other']
const MOOD_OPTIONS = ['Angry', 'Very Angry', 'Cool', 'Okay', 'Satisfied', 'Happy']

export default {
  name: 'CustomerDetails',
  components: { AppConfirmDialog, AppPagination, AppDropdown },
  data () {
    return {
      dashboard: {},
      isLoading: false,
      errorMessage: '',
      lastUpdatedAt: null,
      tabData: {
        orders: { items: [], page: 1, totalPages: 1 },
        calls: { items: [], page: 1, totalPages: 1 },
        medical_records: { items: [], page: 1, totalPages: 1 },
        tickets: { items: [], page: 1, totalPages: 1 },
        timeline: { items: [], page: 1, totalPages: 1 }
      },
      addTicketModal: false,
      isSavingTicket: false,
      resultModal: { show: false, title: '', message: '', color: 'success' },
      newTicket: this.emptyTicket()
    }
  },
  computed: {
    customerId () {
      return this.$route.params.id
    },
    activeTab () {
      const tab = this.$route.params.tab || 'orders'
      return ALLOWED_TABS.includes(tab) ? tab : 'orders'
    },
    activeTabLabel () {
      return TAB_LABELS[this.activeTab] || this.activeTab
    },
    isPaginatedTab () {
      return PAGINATED_TABS.includes(this.activeTab)
    },
    activeItems () {
      return this.tabData[this.activeTab]?.items || []
    },
    activePage () {
      return this.tabData[this.activeTab]?.page || 1
    },
    activeTotalPages () {
      return this.tabData[this.activeTab]?.totalPages || 1
    },
    customerName () {
      return this.dashboard?.contact?.name || ''
    },
    customerPhone () {
      return this.dashboard?.contact?.phone || ''
    },
    customerAddress () {
      const contact = this.dashboard?.contact || {}
      const addr = Array.isArray(contact.address) ? contact.address[0] : null
      if (!addr) return ''
      const parts = [addr.address, addr.post_office_name, addr.district, addr.state, addr.pincode].filter(Boolean)
      return parts.length ? parts.join(', ') : ''
    },
    avatarInitials () {
      const source = this.customerName || `Customer ${this.customerId || ''}`
      const parts = source.trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase() || '?'
    },
    avatarGradient () {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
      ]
      const seed = String(this.customerName || this.customerId || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      return palette[hash % palette.length]
    },
    summaryCards () {
      const orders = Number(this.dashboard?.orders_count || 0)
      const calls = Number(this.dashboard?.calls_count || 0)
      const bottles = Number(this.dashboard?.contact?.bottles || 0)
      const returnRate = Number(this.dashboard?.return_rate || 0)
      return [
        { key: 'orders', label: 'Orders', value: orders },
        { key: 'calls', label: 'Calls', value: calls },
        { key: 'bottles', label: 'Bottles', value: bottles },
        { key: 'returns', label: 'Return Rate', value: `${returnRate}%` }
      ]
    },
    tabs () {
      return ALLOWED_TABS.map((key) => ({
        key,
        label: TAB_LABELS[key],
        count: this.tabCount(key)
      }))
    },
    isModerator () {
      const roles = String(localStorage.getItem('roles') || '')
      return roles.toLowerCase().split(',').map((v) => v.trim()).includes('moderator')
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    productDropdownOptions () {
      return PRODUCT_OPTIONS.map((value) => ({ value, label: value }))
    },
    ticketTypeDropdownOptions () {
      return TICKET_TYPE_OPTIONS.map((value) => ({ value, label: value }))
    },
    reasonDropdownOptions () {
      return REASON_OPTIONS.map((value) => ({ value, label: value }))
    },
    moodDropdownOptions () {
      return MOOD_OPTIONS.map((value) => ({ value, label: value }))
    },
    resultModalVariant () {
      const color = String(this.resultModal.color || '').toLowerCase()
      if (color === 'success') return 'success'
      if (color === 'danger' || color === 'warning') return color
      return 'info'
    }
  },
  watch: {
    '$route.params.tab': {
      handler () {
        this.ensureValidTab()
        if (!this.tabData[this.activeTab].items.length) {
          this.fetchActiveTabData()
        }
      },
      immediate: false
    }
  },
  created () {
    this.ensureValidTab()
    this.fetchDashboard()
    this.fetchActiveTabData()
  },
  methods: {
    token () {
      return localStorage.getItem('api_token')
    },
    emptyTicket () {
      return {
        product_used: '',
        crop: '',
        ticket_type: '',
        reason: '',
        customer_mood: '',
        is_fertilizer_user: false,
        description: ''
      }
    },
    goBack () {
      if (this.$router) {
        this.$router.push({ path: '/customers' })
        return
      }
      window.history.back()
    },
    tabCount (tab) {
      if (PAGINATED_TABS.includes(tab)) {
        return Number(this.dashboard?.[`${tab.split('_')[0]}_count`] || this.tabData[tab]?.items.length || 0)
      }
      return this.tabData[tab]?.items.length || 0
    },
    ensureValidTab () {
      const tab = this.$route.params.tab
      if (tab && !ALLOWED_TABS.includes(tab)) {
        this.$router.replace({ path: `/customer/${this.customerId}/orders` })
      }
    },
    setTab (tab) {
      if (!ALLOWED_TABS.includes(tab) || tab === this.activeTab) return
      this.$router.push({ path: `/customer/${this.customerId}/${tab}` })
    },
    reloadAll () {
      this.fetchDashboard()
      this.fetchActiveTabData()
    },
    fetchDashboard () {
      axios.get(`${this.$apiAdress}/api/contacts/dashboard/${this.customerId}?token=${this.token()}`)
        .then((response) => {
          this.dashboard = response.data || {}
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load customer dashboard.'
        })
    },
    fetchActiveTabData () {
      this.errorMessage = ''
      this.isLoading = true

      const tab = this.activeTab
      const page = this.tabData[tab].page || 1

      let request
      if (tab === 'orders') {
        request = axios.get(`${this.$apiAdress}/api/contacts/orders/${this.customerId}?page=${page}&token=${this.token()}`)
      } else if (tab === 'calls') {
        request = axios.get(`${this.$apiAdress}/api/contacts/calls/${this.customerId}?page=${page}&token=${this.token()}`)
      } else if (tab === 'medical_records') {
        request = axios.get(`${this.$apiAdress}/api/contacts/crops/${this.customerId}?page=${page}&token=${this.token()}`)
      } else if (tab === 'tickets') {
        request = axios.post(`${this.$apiAdress}/api/tickets_by_customer?token=${this.token()}`, {
          contact_id: this.customerId
        })
      } else {
        request = axios.get(`${this.$apiAdress}/api/contacts/timeline/${this.customerId}?token=${this.token()}`)
      }

      request
        .then((response) => {
          const payload = response.data || {}

          if (tab === 'tickets' || tab === 'timeline') {
            this.tabData[tab].items = Array.isArray(payload)
              ? payload
              : Array.isArray(payload.data) ? payload.data : []
            this.tabData[tab].page = 1
            this.tabData[tab].totalPages = 1
            return
          }

          this.tabData[tab].items = Array.isArray(payload.data) ? payload.data : []
          this.tabData[tab].page = Number(payload.current_page || page)
          this.tabData[tab].totalPages = Number(payload.last_page || 1)
        })
        .catch((error) => {
          console.log(error)
          this.tabData[tab].items = []
          this.errorMessage = `Unable to load ${TAB_LABELS[tab].toLowerCase()}.`
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onPageChange (page) {
      const target = Number(page || 1)
      if (target === this.tabData[this.activeTab].page) return
      this.tabData[this.activeTab].page = target
      this.fetchActiveTabData()
    },
    openAddTicketModal () {
      this.newTicket = this.emptyTicket()
      this.addTicketModal = true
    },
    cancelAddTicket () {
      if (this.isSavingTicket) return
      this.addTicketModal = false
    },
    onTicketTypeSelect (value) {
      this.newTicket.ticket_type = value
      if (value !== 'Result') this.newTicket.reason = ''
    },
    saveTicket () {
      this.isSavingTicket = true

      const payload = {
        contact_id: this.customerId,
        product_used: this.newTicket.product_used,
        crop: this.newTicket.crop,
        ticket_type: this.newTicket.ticket_type,
        reason: this.newTicket.reason,
        customer_mood: this.newTicket.customer_mood,
        is_fertilizer_user: this.newTicket.is_fertilizer_user ? 1 : 0,
        description: this.newTicket.description
      }

      axios.post(`${this.$apiAdress}/api/tickets?token=${this.token()}`, payload)
        .then((response) => {
          const data = response.data || {}
          this.resultModal = {
            show: true,
            title: data.title || 'Success',
            message: data.message || 'Ticket created successfully.',
            color: data.color || 'success'
          }
          this.addTicketModal = false
          if (this.activeTab === 'tickets') {
            this.fetchActiveTabData()
          } else {
            this.tabData.tickets.items = []
          }
        })
        .catch((error) => {
          const data = error?.response?.data || {}
          const validation = data?.error && typeof data.error === 'object'
            ? Object.values(data.error).flat().join(', ')
            : ''
          this.resultModal = {
            show: true,
            title: data.title || 'Error',
            message: data.message || validation || 'Unable to create ticket.',
            color: data.color || 'warning'
          }
        })
        .finally(() => {
          this.isSavingTicket = false
        })
    },
    closeResultModal () {
      this.resultModal.show = false
    },
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatDate (value) {
      if (!value) return '—'
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value)) {
        const [d, m, y] = value.split('-').map(Number)
        return `${d} ${months[m - 1] || m} ${y}`
      }
      const dt = new Date(value)
      if (isNaN(dt.getTime())) return String(value)
      return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`
    },
    formatDateTime (value) {
      if (!value) return '—'
      const dt = new Date(value)
      if (isNaN(dt.getTime())) return String(value)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const time = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
      return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}, ${time}`
    },
    formatDuration (seconds) {
      const total = Math.max(0, Number(seconds || 0))
      const m = Math.floor(total / 60)
      const s = total % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },
    shippingStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'delivered') return 'pill--green'
      if (s === 'shipped') return 'pill--blue'
      if (s === 'returned' || s === 'cancelled' || s === 'manifest_failed') return 'pill--red'
      if (s === 'for_print' || s === 'for_picking' || s === 'packed') return 'pill--amber'
      return 'pill--gray'
    },
    orderTypeClass (type) {
      const t = String(type || '').toLowerCase()
      if (t === 'telesale' || t === 'tele') return 'pill--blue'
      if (t === 'ecommerce' || t === 'ecom') return 'pill--violet'
      if (t === 'agency') return 'pill--teal'
      if (t === 'direct_sale' || t === 'direct') return 'pill--green'
      if (t === 'store_sale' || t === 'store' || t === 'amazon') return 'pill--amber'
      return 'pill--gray'
    },
    callStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('sold') || s === 'closed') return 'pill--green'
      if (s.includes('not_interested')) return 'pill--gray'
      if (s.includes('not_attended')) return 'pill--amber'
      if (s.includes('feedback') || s.includes('follow')) return 'pill--blue'
      return 'pill--gray'
    },
    ticketTypeClass (type) {
      if (type === 'No Result') return 'pill--red'
      if (type === 'Result') return 'pill--green'
      if (type === 'Other Complaint') return 'pill--amber'
      return 'pill--gray'
    },
    moodClass (mood) {
      const m = String(mood || '').toLowerCase()
      if (m === 'angry' || m === 'very angry') return 'pill--red'
      if (m === 'cool' || m === 'okay') return 'pill--blue'
      if (m === 'satisfied' || m === 'happy') return 'pill--green'
      return 'pill--gray'
    },
    timelineTypeClass (type) {
      const t = String(type || '').toLowerCase()
      if (t === 'order') return 'pill--blue'
      if (t === 'call') return 'pill--green'
      if (t === 'ticket') return 'pill--amber'
      return 'pill--gray'
    }
  }
}
</script>

<style scoped>
.cust-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.cust-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.cust-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.cust-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.cust-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
  word-break: break-word;
}

.cust-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.cust-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.cust-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.cust-toolbar__actions >>> .btn.back-btn,
.cust-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cust-toolbar__actions >>> .btn.back-btn:hover:not(:disabled),
.cust-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.cust-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.back-btn__icon,
.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.cust-toolbar__actions >>> .btn.back-btn:hover:not(:disabled) .back-btn__icon {
  transform: translateX(-2px);
}

.cust-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.cust-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

.cust-toolbar__actions >>> .btn.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.cust-toolbar__actions >>> .btn.create-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.create-btn__icon {
  flex-shrink: 0;
  display: block;
}

/* ── Alert ── */
.cust-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Hero ── */
.cust-hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
}

.cust-hero__avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: 0.04em;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
}

.cust-hero__main {
  min-width: 0;
  flex: 1;
}

.cust-hero__name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin-bottom: 0.4rem;
}

.cust-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.cust-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #f1f5f9;
  color: #1f2a44;
}

.cust-chip--phone {
  background: #dff5ea;
  color: #0e5b46;
}

.cust-chip--addr {
  background: #e0edff;
  color: #1e40af;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--orders   { border-left-color: #1f2a44; }
.summary-tile--calls    { border-left-color: #16a07a; }
.summary-tile--bottles  { border-left-color: #6d28d9; }
.summary-tile--returns  { border-left-color: #c0392b; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Section card ── */
.cust-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.cust-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  overflow-x: auto;
}

.cust-tabs {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cust-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.95rem;
  border: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cust-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.cust-tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.22);
}

.cust-tab.is-active {
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  color: #ffffff;
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(14, 91, 70, 0.28));
}

.cust-tab__count {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: rgba(14, 91, 70, 0.10);
  color: #0e5b46;
}

.cust-tab.is-active .cust-tab__count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.cust-section__body {
  padding: 1rem;
}

/* ── Loading / empty ── */
.cust-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.cust-empty {
  text-align: center;
  padding: 3rem 1rem;
}

.cust-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.cust-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

/* ── Table ── */
.cust-table-wrap {
  overflow-x: auto;
}

.cust-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.cust-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fbfcfe;
  border-top: 0;
  border-bottom: 2px solid #e5e9f2;
  padding: 0.65rem 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #374151;
  white-space: nowrap;
  text-align: left;
}

.cust-table tbody tr {
  transition: background-color 0.12s ease;
}

.cust-table tbody tr:hover {
  background: #f8fafc;
}

.cust-table td {
  padding: 0.75rem 0.9rem;
  border-bottom: 1px solid #eef1f7;
  vertical-align: middle;
  color: #374151;
}

.col-num      { width: 96px; text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; color: #1f2a44; }
.col-date     { white-space: nowrap; color: #4b5563; font-variant-numeric: tabular-nums; }
.col-phone    { white-space: nowrap; font-variant-numeric: tabular-nums; }
.col-comments { color: #4b5563; max-width: 320px; }

.mono-strong {
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
}

/* ── Pills (status / type / mood) ── */
.status-pill,
.type-pill,
.mood-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.pill--gray   { background: #f1f5f9; color: #475569; }
.pill--violet { background: #ede9fe; color: #5b21b6; }
.pill--blue   { background: #dbeafe; color: #1e40af; }
.pill--amber  { background: #fef3c7; color: #92400e; }
.pill--teal   { background: #ccfbf1; color: #115e59; }
.pill--green  { background: #dff5ea; color: #0e5b46; }
.pill--red    { background: #fee2e2; color: #991b1b; }

/* ── Pagination ── */
.cust-pagination {
  padding: 0.75rem 1rem;
  border-top: 1px solid #eef1f7;
}

/* ── Form fields (Add Ticket dialog) ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field--check {
  grid-column: 1 / -1;
  align-self: center;
}

.form-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-check {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #1f2a44;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.form-check input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #16a07a;
  cursor: pointer;
  margin: 0;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .cust-toolbar {
    align-items: flex-start;
  }

  .cust-hero {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .summary-tiles {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
